import {ErrorHandler, Injectable, LOCALE_ID, NgModule} from "@angular/core";
import {GlobalErrorHandler} from "./_shared/global-error-handler";
import {BrowserModule} from "@angular/platform-browser";
import {AppRoutingModule} from "./app-routing.module";
import {AppComponent} from "./app.component";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {provideHttpClient, withInterceptors} from "@angular/common/http";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatButtonModule} from "@angular/material/button";
import {MatSidenavModule} from "@angular/material/sidenav";
import {MatIconModule} from "@angular/material/icon";
import {MatListModule} from "@angular/material/list";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MAT_RIPPLE_GLOBAL_OPTIONS,
  MatNativeDateModule,
  RippleGlobalOptions
} from "@angular/material/core";

import {initializeApp, provideFirebaseApp} from "@angular/fire/app";
import {getFirestore, provideFirestore} from "@angular/fire/firestore";

import {getStorage, provideStorage} from "@angular/fire/storage";
import {MatProgressBarModule} from "@angular/material/progress-bar";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";

import {NgxEditorModule} from "ngx-editor";
import {environment} from "../environments/environment";
import {getAuth, provideAuth} from "@angular/fire/auth";
import {authInterceptor} from "./_interceptors/auth.interceptor";
import {provideAnimationsAsync} from "@angular/platform-browser/animations/async";
import {registerLocaleData} from "@angular/common";
import localeNl from "@angular/common/locales/nl";
import localeNlExtra from '@angular/common/locales/extra/nl';
import {Settings} from "luxon";
import {MatPaginatorIntl} from "@angular/material/paginator";
import {CustomNativeDateAdapter} from "./_shared/custom-date.adapter";
import {DragDropModule} from '@angular/cdk/drag-drop';
import {NgxSkeletonLoaderModule} from "ngx-skeleton-loader";




/**
 * The globalRippleConfig variable is a configuration object for the global Ripple effect in the application.
 *
 * @type {RippleGlobalOptions}
 * @property {boolean} disabled - Indicates whether the global Ripple effect is disabled. Defaults to true.
 * @property {Object} animation - Specifies the animation properties for the Ripple effect.
 *     @property {number} enterDuration - The duration of the enter animation in milliseconds. Defaults to 300.
 *     @property {number} exitDuration - The duration of the exit animation in milliseconds. Defaults to 0.
 */
const globalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 300,
    exitDuration: 0
  }
};


// Set default timezone and locale for Luxon
if (environment.language === "nl") {
  registerLocaleData(localeNl, 'nl-NL', localeNlExtra);
  Settings.defaultLocale = "nl";
}
Settings.defaultZone = "Europe/Amsterdam"; // Amsterdam Timezone

const DATE_FORMATS = environment.language === 'nl'
  ? {
    parse: { dateInput: 'dd-MM-yyyy' },
    display: {
      dateInput: 'dd-MM-yyyy',
      monthYearLabel: 'MMMM yyyy',
      dateA11yLabel: 'dd-MM-yyyy',
      monthYearA11yLabel: 'MMMM yyyy',
    }
  }
  : {
    parse: { dateInput: 'MM/dd/yyyy' },
    display: {
      dateInput: 'MM/dd/yyyy',
      monthYearLabel: 'MMMM yyyy',
      dateA11yLabel: 'MM/dd/yyyy',
      monthYearA11yLabel: 'MMMM yyyy',
    }
  };



@Injectable()
export class CustomPaginatorIntl extends MatPaginatorIntl {
  override itemsPerPageLabel = environment.language === "nl" ? "Items per pagina" : "Items per page";
  override nextPageLabel = environment.language === "nl" ? "Volgende pagina" : "Next page";
  override previousPageLabel = environment.language === "nl" ? "Vorige pagina" : "Previous page";
}

/**
 * The AppModule class is the root module of the application. It is responsible for bootstrapping the application and declaring the
 * necessary components, importing required modules, and providing services.
 *
 * The main purpose of this AppModule is to configure and initialize the Angular application. It is defined with the `@NgModule` decorator
 * and contains metadata about the module.
 *
 * @NgModule - Angular decorator that defines the AppModule class as a module and specifies the metadata for the module.
 *
 * @param {declarations[]} - An array of component classes, directives, and pipes that belong to this module.
 * @param {bootstrap[]} - An array of components that are the starting point of the application.
 * @param {imports[]} - An array of modules to import into this module.
 * @param {providers[]} - An array of service providers for this module.
 *
 * @returns {AppModule} - The AppModule class is exported to be used by other parts of the application.
 */
@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    NgxEditorModule.forRoot({
      locals: {
        // menus
        bold: "Bold",
        italic: "Italic",
        code: "Code",
        blockquote: "Blockquote",
        underline: "Underline",
        strike: "Strike",
        bullet_list: "Bullet List",
        ordered_list: "Ordered List",
        heading: $localize`:@@editor-heading:Heading`,
        h1: $localize`:@@editor-heading-1:Heading 1`,
        h2: $localize`:@@editor-heading-2:Heading 2`,
        h3: $localize`:@@editor-heading-3:Heading 3`,
        h4: $localize`:@@editor-heading-4:Heading 4`,
        h5: $localize`:@@editor-heading-5:Heading 5`,
        h6: $localize`:@@editor-heading-6:Heading 6`,
        align_left: "Left Align",
        align_center: "Center Align",
        align_right: "Right Align",
        align_justify: "Justify",
        text_color: "Text Color",
        background_color: "Background Color",
        // popups, forms, others...
        url: "URL",
        text: "Text",
        openInNewTab: "Open in new tabs",
        insert: "Insert",
        altText: "Alt Text",
        title: "Title",
        remove: "Remove",
      },
    }),
    MatToolbarModule,
    DragDropModule,
    MatButtonModule,
    MatSidenavModule,
    MatNativeDateModule,
    MatIconModule,
    MatListModule,
    NgxSkeletonLoaderModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
  ],
  providers: [
    {
      provide: LOCALE_ID,
      useValue: (environment.language === "nl" ? "nl-NL" : "en-US")
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: (environment.language === "nl" ? "nl-NL" : "en-US")
    },
    {
      provide: MatPaginatorIntl,
      useClass: CustomPaginatorIntl
    },
    {
      provide: DateAdapter,
      useClass: CustomNativeDateAdapter
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: DATE_FORMATS,
    },
    provideHttpClient(withInterceptors([
      authInterceptor,
    ])),
    provideAnimationsAsync(),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideStorage(() => getStorage()),
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: globalRippleConfig
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
}
